import React, { useContext, useState } from 'react';
import {
  Alert, Button, Checkbox, Form, InputNumber, notification,
} from 'antd';
import * as API from 'aws-amplify/api';
import { ConsoleLogger as Logger } from 'aws-amplify/utils';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Decimal from 'decimal.js';
import { InfoCircleOutlined } from '@ant-design/icons';
import { createErrorMessage } from '../../context/globalStateHelper';
import ModalWrapper, { modalConfirmClose, modalConfirmSave } from '../ModalWrapper';
import Context from '../../context';
import { INITIAL_AMOUNTS } from '../../constants/gasTaxDeclarations';
import { UNITS } from '../../constants/units';
import { formatNumber } from '../../utils/i18n';
import {
  getAllowedGasTaxClassNames,
  getMeteringSiteConsumptionKwh,
  getLatestDeclarationAmounts,
} from '../DeclareGasTaxTable/utils';

import './DeclareGasTaxModal.less';

const logger = new Logger('new-biogas-tax-declaration'); // TODO logger name ok?

// Mirrors tax-service's PG02_RESIDUAL_TOLERANCE_KWH: declared amounts are
// whole kWh (MWh input has 3-decimal precision) but metered consumption is
// fractional, so a declaration matching the consumption shown on this page
// (itself rounded to 3 decimals for display) can be a fraction of a kWh over
// the true raw value without being a real overshoot.
const CONSUMPTION_ROUNDING_TOLERANCE_KWH = 1;

const DeclareGasTaxModal = () => {
  const {
    isAdmin,
    isAdminAsMarketParty,
    isReadOnlyAdmin,
    selectedMarketPartyId,
    meteringSites,
    meteringSiteConsumptions: {
      data: consumptionsData,
      requestParams: {
        start,
      },
    },
    taxDeclarations,
    declareStart,
    declareEnd,
  } = useContext(Context);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const period = moment(start)
    .format('YYYY-MM');

  // Prefill with the latest declaration for this point + period, if one
  // exists — a lazy initializer so the modal never flashes empty and then
  // fills in; taxDeclarations is already loaded by the time this opens.
  const [amounts, setAmounts] = useState(
    () => getLatestDeclarationAmounts(taxDeclarations, id, period) || INITIAL_AMOUNTS,
  );
  const [saving, setSaving] = useState(false);
  const [overshootConfirmed, setOvershootConfirmed] = useState(false);
  // Prefilled amounts aren't a user change — only an actual edit should ask
  // "discard changes?" on close.
  const [userHasEdited, setUserHasEdited] = useState(false);

  const selectedMeteringSite = (meteringSites || []).find(site => site.id === id);
  const meteringSiteName = selectedMeteringSite ? selectedMeteringSite.name : id;

  const currentDate = Date.now();
  const canDeclare = isAdmin() || isAdminAsMarketParty() || (currentDate > Date.parse(declareStart)
    && currentDate <= Date.parse(declareEnd));

  const filterEmptyAmounts = () => amounts.filter(item => item.amount);
  const isEmpty = () => !filterEmptyAmounts().length;

  // Declared biogas/CHP must not exceed the metered natural gas consumption
  // for this metering site and period (both in kWh HHV) — a real overshoot
  // (beyond the backend's sub-kWh rounding tolerance) excludes the whole
  // point from the calculation, so the equal-to-consumption boundary is
  // still allowed. Admin-as-market-party users act on the customer's behalf
  // and can check the corrected consumption in the OPS Portal first, so
  // they're exempt from the block but still see a warning and must confirm
  // it explicitly.
  const declaredSumKwh = amounts.reduce(
    (sum, { amount }) => (typeof amount === 'number' ? sum.add(amount) : sum),
    new Decimal(0),
  );
  const consumptionKwh = getMeteringSiteConsumptionKwh(consumptionsData, id);
  // consumptionKwh is null while this site's consumption hasn't loaded yet —
  // treat that as "can't validate" rather than "zero consumption", or the
  // check would block Save before there's anything to compare against.
  const exceedsConsumption = consumptionKwh !== null
    && declaredSumKwh.greaterThan(consumptionKwh.add(CONSUMPTION_ROUNDING_TOLERANCE_KWH));
  const blocksSave = exceedsConsumption && !isAdminAsMarketParty();
  const needsOvershootConfirmation = exceedsConsumption && isAdminAsMarketParty();
  const canSave = !blocksSave && (!needsOvershootConfirmation || overshootConfirmed);

  const closeModal = () => navigate(location.pathname.replace(`/${id}`, ''));
  const handleClose = () => (
    (isEmpty() || !userHasEdited) ? closeModal() : modalConfirmClose(t, closeModal)
  );

  const handleSave = async () => {
    setSaving(true);

    // Don't save if user isn't allowed to make declarations
    // TODO are the conditions ok?
    if (selectedMarketPartyId === undefined || isAdmin() || isReadOnlyAdmin() || !canSave) {
      setSaving(false);
      return;
    }
    try {
      const payload = {
        period,
        meteringPointId: id,
        amounts: filterEmptyAmounts(),
      };
      await API.post({
        apiName: 'FINTSO',
        path: `/marketparties/${selectedMarketPartyId}/meteringsites/taxdeclarations`,
        options: { body: payload },
      }).response;
      notification.success({
        message: t('declareGasTax.message.createdSuccessfully'),
        description: '',
      });
      closeModal();
    } catch (error) {
      notification.error({
        className: 'notification-error',
        message: t('declareGasTax.message.errorCreating'),
        description: await createErrorMessage(error),
      });
      logger.error(await createErrorMessage(error, true));
    }
    setSaving(false);
  };

  const multiplier = new Decimal(0.001);
  const convertValue = (value, outputUnit = UNITS.KWH) => Number(
    outputUnit === UNITS.MWH
      ? multiplier.mul(value)
      : new Decimal(1).div(multiplier)
        .mul(value),
  );

  const handleChange = (value, taxClassName) => {
    const amount = typeof value === 'number'
      ? convertValue(value, UNITS.KWH)
      : null;
    setAmounts(
      prevState => prevState.map(
        taxClass => (
          taxClass.type === taxClassName
            ? {
              type: taxClassName,
              amount,
            }
            : taxClass
        ),
      ),
    );
    // A confirmation only covers the sum it was given for — any further
    // edit needs a fresh look at the (possibly now larger) overshoot.
    setOvershootConfirmed(false);
    setUserHasEdited(true);
  };

  const calculateValue = (taxClassName) => {
    const value = amounts.find(taxClass => taxClass.type === taxClassName).amount;
    return typeof value === 'number' ? convertValue(value, UNITS.MWH) : null;
  };

  const renderTaxClassFields = () => getAllowedGasTaxClassNames().map(
    taxClassName => (
      <>
        <Form.Item
          className={`tax-declarations__${taxClassName}-item`}
          key={taxClassName}
          label={`${t(`declareGasTax.modal.${taxClassName}`)}`}
        >
          <InputNumber
            key={taxClassName}
            className={`tax-declarations__input tax-declarations__${taxClassName}-input`}
            min={0}
            precision={3}
            step={1}
            decimalSeparator=","
            value={calculateValue(taxClassName)}
            onChange={value => handleChange(value, taxClassName)}
          />
          &nbsp;MWh
        </Form.Item>
      </>
    ),
  );

  // Deliberately a plain div, not a <Form.Item> — a Form.Item here would
  // inherit the form's wrapperCol={{ span: 10 }}, squeezing the alert into
  // a narrow column instead of the full width. antd only applies that
  // treatment to Form.Item children, so a bare div inside <Form> is unaffected.
  const renderConsumptionOvershoot = () => exceedsConsumption && (
    <div className="declaregastax-modal__overshoot">
      <Alert
        type={needsOvershootConfirmation ? 'warning' : 'error'}
        showIcon
        message={t(
          needsOvershootConfirmation
            ? 'declareGasTax.modal.validation.exceedsConsumptionWarning'
            : 'declareGasTax.modal.validation.exceedsConsumptionError',
          {
            declared: formatNumber(convertValue(declaredSumKwh, UNITS.MWH), 0, 3),
            consumption: formatNumber(convertValue(consumptionKwh, UNITS.MWH), 0, 3),
          },
        )}
      />
      { needsOvershootConfirmation && (
        <Checkbox
          className="declaregastax-modal__overshoot-confirm"
          checked={overshootConfirmed}
          onChange={event => setOvershootConfirmed(event.target.checked)}
        >
          {t('declareGasTax.modal.validation.confirmOvershootLabel')}
        </Checkbox>
      )}
    </div>
  );

  const actionButtons = [
    <Button
      className="tax-declarations__cancel-button"
      key="closeButton"
      onClick={() => handleClose()}
    >
      {t('common.button.close')}
    </Button>,
    <Button
      className="tax-declarations__save-button"
      key="saveButton"
      disabled={!canSave}
      loading={saving}
      type="primary"
      onClick={() => modalConfirmSave(t, handleSave)}
    >
      {t('common.button.save')}
    </Button>,
  ];

  return canDeclare && (
    <ModalWrapper
      modalClassName="declaregastax-modal"
      title={t('declareGasTax.title')}
      actionButtons={actionButtons}
      handleClose={handleClose}
    >
      <>
        <h2>{`${meteringSiteName} (${id})`}</h2>
        <h3>{moment(period).format('MMMM YYYY')}</h3>
        <div>
          <Form
            layout="horizontal"
            labelAlign="left"
            labelCol={{ span: 13 }}
            wrapperCol={{ span: 10 }}
          >
            {renderTaxClassFields()}
            {renderConsumptionOvershoot()}
          </Form>
        </div>
        <div className="declaregastax-modal__help">
          <InfoCircleOutlined />
          <span>{t('declareGasTax.modal.help')}</span>
        </div>
      </>
    </ModalWrapper>
  );
};

export default DeclareGasTaxModal;
